import React, { useState } from 'react';

import ProjectDashboardActivitiesByCategory from './ProjectDashboardActivitiesByCategory';
import ProjectDashboardActivityTimeline from './ProjectDashboardActivityTimeline';
import ProjectDashboardExecutiveSummary from './ProjectDashboardExecutiveSummary';
import ProjectDashboardKeyMilestones, { DEFAULT_WINDOW_DAYS } from './ProjectDashboardKeyMilestones';
import ProjectDashboardSummary from './ProjectDashboardSummary';

import useQuery from 'Hooks/useQuery';
import { useToast } from 'Hooks/useToast';

export default function ProjectDashboardIndex(props) {
    const [periodInMonths, setPeriodInMonths] = useState(6);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [windowDays, setWindowDays] = useState(DEFAULT_WINDOW_DAYS);
    const [keyMilestonesOnly, setKeyMilestonesOnly] = useState(true);

    const toast = useToast();

    const dashboardDataRequest = useQuery({
        // Read the target page and the milestone controls from the refetch argument to
        // avoid a stale closure over the state they mirror; fall back to state for the
        // initial load.
        queryFn: (p) =>
            axios.get(props.dataUrl, {
                params: {
                    period: periodInMonths,
                    search,
                    page: p?.page ?? page,
                    window_days: p?.window_days ?? windowDays,
                    // 1/0, not true/false: axios serialises a boolean query parameter
                    // to the string "false", which Laravel's `boolean` rule rejects
                    // outright — the request would 422 rather than turn the toggle off.
                    key_milestones_only: (p?.key_milestones_only ?? keyMilestonesOnly) ? 1 : 0,
                },
            }),
    });

    const changeSearch = (e) => {
        const value = e.target.value;
        setSearch(value);
    };

    const clearSearch = () => {
        setSearch('');
    };

    const changePeriod = (e) => {
        const val = e.target.value;
        setPeriodInMonths(val);
    };

    // Both milestone controls are server-side: the window moves the panel's upper bound
    // and the "due" tile with it, and the toggle changes which activity types the query
    // asks for. Neither can be done by filtering what is already on screen, so each
    // refetches with its new value passed through rather than read back from state,
    // which has not updated yet at this point.
    const changeWindowDays = (e) => {
        const val = Number(e.target.value);
        setWindowDays(val);
        dashboardDataRequest.refetch({ window_days: val });
    };

    const changeKeyMilestonesOnly = (val) => {
        setKeyMilestonesOnly(val);
        dashboardDataRequest.refetch({ key_milestones_only: val });
    };

    const updateActivityField = (key, value, activityId) => {
        const url = props.updateActivityUrl.replace('ACTIVITY_ID', activityId);
        let data = {
            key: key,
            value: value,
        };
        axios
            .put(url, data)
            .then((res) => {
                toast.success($t('Activity updated successfully'));
            })
            .catch((e) => {
                if (e.response) {
                    toast.error($t('Something went wrong while updating the {{item}}', { item: 'activity' }));
                }
            });
    };

    return (
        <div className="tw-space-y-4">
            {/* The strip takes no CTA props — its empty state is deliberately buttonless. */}
            <ProjectDashboardExecutiveSummary
                loading={dashboardDataRequest.loading}
                data={dashboardDataRequest.data?.executive_summary}
                windowDays={windowDays}
            />
            <ProjectDashboardKeyMilestones
                loading={dashboardDataRequest.loading}
                data={dashboardDataRequest.data?.key_milestones || []}
                viewProjectActivityDetailsUrl={props.viewProjectActivityDetailsUrl}
                canManage={props.canManage}
                importProjectPlanUrl={props.importProjectPlanUrl}
                onAddActivity={props.onAddActivity}
                windowDays={windowDays}
                changeWindowDays={changeWindowDays}
                keyMilestonesOnly={keyMilestonesOnly}
                changeKeyMilestonesOnly={changeKeyMilestonesOnly}
            />
            <ProjectDashboardSummary
                loading={dashboardDataRequest.loading}
                data={dashboardDataRequest.data?.owner_activities || []}
                minDate={props.minDate}
                maxDate={props.maxDate}
                fetchProjectActivitiesUrl={props.fetchProjectActivitiesUrl}
                onChangeHandler={updateActivityField}
                canManage={props.canManage}
                importProjectPlanUrl={props.importProjectPlanUrl}
                onAddActivity={props.onAddActivity}
            />
            <ProjectDashboardActivityTimeline loading={dashboardDataRequest.loading} data={dashboardDataRequest.data?.milestone_activities || {}} />
            <ProjectDashboardActivitiesByCategory
                viewProjectActivityDetailsUrl={props.viewProjectActivityDetailsUrl}
                loading={dashboardDataRequest.loading}
                activities={dashboardDataRequest.data?.activities_by_outlook || {}}
                periodInMonths={periodInMonths}
                changePeriod={changePeriod}
                page={page}
                setPage={setPage}
                fetchData={(p) => dashboardDataRequest.refetch(p)}
                search={search}
                changeSearch={changeSearch}
                clearSearch={clearSearch}
            />
        </div>
    );
}
